import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '@services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, of, switchMap, tap, timer } from 'rxjs';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'ew-edit-user-name',
  templateUrl: './edit-user-name.component.html',
  styleUrl: './edit-user-name.component.scss',
})
export class EditUserNameComponent implements OnInit {
  loading = false;
  userId: string = '';
  userNameControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(255),
  ]);
  currentName: string = '';
  currentEmail: string = '';

  constructor(
    public dialogRef: MatDialogRef<EditUserNameComponent>,
    private API: ApiService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    public data: { userId: string; email: string; userName?: string },
  ) {}

  ngOnInit(): void {
    this.userId = this.data.userId;
    this.currentEmail = this.data.email;

    if (this.data.userName) {
      this.userNameControl.setValue(this.data.userName);
    } else {
      this.loading = true;
      this.API.get(`v1/users/${this.userId}`)
        .pipe(
          catchError((error) => {
            console.log(
              {
                level: 'error',
                message: 'Failed to fetch user details',
                payload: this.userId,
                error: {
                  message: error.message,
                  stack: error.stack,
                },
              },
              'EDIT_USER_NAME | ERROR',
            );
            this.loading = false;
            return of(null);
          }),
        )
        .subscribe((userData) => {
          this.loading = false;
          if (userData && userData.userName) {
            this.currentName = userData.userName;
            this.currentEmail = userData.email;
            this.userNameControl.setValue(userData.userName);
          }
        });
    }
  }

  editUserName(): void {
    if (this.userNameControl.invalid) {
      return;
    }

    const newUserName = this.userNameControl.value;

    if (newUserName === this.currentName) {
      this.dialogRef.close(false);
      return;
    }

    this.loading = true;

    this.API.patch(`v1/users/${this.userId}/username`, {
      userName: newUserName,
    })
      .pipe(
        tap(() => {
          console.log(
            {
              level: 'info',
              message: 'User name updated successfully',
              payload: {
                userId: this.userId,
                oldName: this.currentName,
                newUserName,
              },
            },
            'EDIT_USER_NAME | INFO',
          );

          this.API.alertService.success(
            this.translate.instant('alert:editUserNameSuccess'),
          );
        }),
        switchMap(() =>
          timer(2500).pipe(tap(() => this.dialogRef.close(true))),
        ),
        catchError((error) => {
          console.log(
            {
              level: 'error',
              message: 'Failed to update user name',
              payload: this.userId,
              error: {
                message: error.message,
                stack: error.stack,
              },
            },
            'EDIT_USER_NAME | ERROR',
          );

          this.loading = false;
          return of(null);
        }),
      )
      .subscribe();
  }
}
